import { createSlice } from '@reduxjs/toolkit'

import { profileApi } from '@core/app/api/profileApi'
import { newProfileApi } from '@core/app/api/v2/newProfileApi'
import { RootState } from '@core/app/store/store'
import {
    AuthMethods,
    ContactPreferencesData,
    DeletionRequestData,
    LastLoginData,
    LocaleItem,
    NewProfileData,
    NewProfileFile,
    PhonesData,
    ProfileFieldsDataSettings,
    TermsData,
} from '@core/app/types/v2/newProfile'

const initialProfilePicture = typeof window !== 'undefined' ? window.employeeData?.profile?.picture || null : null

interface NewProfileState {
    profileData: NewProfileData | null
    profilePhones: PhonesData | null
    contactPreferences: ContactPreferencesData[] | null
    loading: boolean
    languageId: number | null
    locales: Record<number, LocaleItem> | null
    terms: TermsData | null
    deletionRequest: DeletionRequestData | null
    settings: ProfileFieldsDataSettings | null
    authMethods: AuthMethods | null
    profilePicture: string | null
    files: NewProfileFile[] | null
    employeeApp: boolean
    lastLogins: LastLoginData[] | null
    isPasswordExists?: boolean | null
}

const initialState: NewProfileState = {
    profileData: null,
    profilePhones: null,
    loading: false,
    languageId: null,
    locales: null,
    contactPreferences: null,
    terms: null,
    deletionRequest: null,
    settings: null,
    authMethods: null,
    profilePicture: initialProfilePicture,
    files: null,
    employeeApp: false,
    lastLogins: null,
    isPasswordExists: null,
}

const newProfileSlice = createSlice({
    name: 'newProfile',
    initialState,
    reducers: {
        setProfilePicture: (state, action: { payload: string }) => {
            state.profilePicture = action.payload
        },
    },
    extraReducers(builder) {
        builder.addMatcher(newProfileApi.endpoints.getProfileData.matchFulfilled, (state, action) => {
            const payload = action.payload
            if (payload.profile !== undefined) state.profileData = payload.profile
            if (payload.phones !== undefined) state.profilePhones = payload.phones
            if (payload.languageId !== undefined) state.languageId = payload.languageId
            if (payload.contacts !== undefined) state.contactPreferences = payload.contacts
            if (payload.terms !== undefined) state.terms = payload.terms
            if (payload.deletionRequest !== undefined) state.deletionRequest = payload.deletionRequest
            if (payload.settings !== undefined) state.settings = payload.settings
            if (payload.authMethods !== undefined) state.authMethods = payload.authMethods
            if (payload.files !== undefined) state.files = [...payload.files].reverse()
            if (payload.employeeApp !== undefined) state.employeeApp = payload.employeeApp.showAppBanner
            if (payload.locale?.list !== undefined) state.locales = payload.locale.list
            if (payload.lastLogins !== undefined) state.lastLogins = payload.lastLogins
            if (payload.password !== undefined) state.isPasswordExists = payload.password.isPasswordExists
            state.loading = false
        })
        builder.addMatcher(profileApi.endpoints.getHeaderInfo.matchFulfilled, (state, action) => {
            state.profilePicture = action.payload.data.ProfilePicture
        })
        builder.addMatcher(newProfileApi.endpoints.getProfileData.matchPending, (state) => {
            state.loading = true
        })
        builder.addMatcher(newProfileApi.endpoints.getProfileData.matchRejected, (state) => {
            state.loading = false
        })
    },
})

export const storeProfileData = (state: RootState) => state.newProfile.profileData
export const storeProfilePhones = (state: RootState) => state.newProfile.profilePhones
export const storeProfileLoading = (state: RootState) => state.newProfile.loading
export const storeLocales = (state: RootState) => state.newProfile.locales
export const storeLanguageId = (state: RootState) => state.newProfile.languageId
export const storeContactPreferences = (state: RootState) => state.newProfile.contactPreferences
export const storeTerms = (state: RootState) => state.newProfile.terms
export const storeDeletionRequest = (state: RootState) => state.newProfile.deletionRequest
export const storeSettings = (state: RootState) => state.newProfile.settings
export const storeAuthMethods = (state: RootState) => state.newProfile.authMethods
export const storeProfilePicture = (state: RootState) => state.newProfile.profilePicture
export const storeProfileFiles = (state: RootState) => state.newProfile.files
export const storeEmployeeApp = (state: RootState) => state.newProfile.employeeApp
export const storeLastLogins = (state: RootState) => state.newProfile.lastLogins
export const storeIsPasswordExists = (state: RootState) => state.newProfile.isPasswordExists
export const { setProfilePicture } = newProfileSlice.actions
export default newProfileSlice.reducer
